import { isBrowserMockRuntimeEnabled } from "../mocks/browserMockRuntime";
import authStore from "../stores/auth/authStore";
import uiStore from "../stores/uiStore";
import { apiClient } from "./apiClient";
import { logger } from "./logger";
import { platformRoutes, resolvePlatformRequestUrl } from "./platformRoutes/platformRoutes";

/**
 * Best-effort client telemetry: POSTs minimal LOGIN / PAGE_VIEW / BUTTON_CLICK
 * events to the backend event-ingest endpoint (`/api/events/:customerNumber`,
 * see EventIngestionController in java-dsp-backend).
 *
 * The backend enriches each event server-side with the trusted context it can
 * derive from the validated session, so the client deliberately sends only the
 * minimal event data the server cannot know on its own.
 *
 * "Best-effort" means: never blocks, never throws, never retries. Network
 * failures (offline, 404 when ingest is disabled, 401, 429, 5xx) are swallowed
 * silently. A synchronous error while building the request is unexpected — it
 * signals a bug in this code — so it is logged at warn level and then swallowed.
 * The endpoint requires an authenticated session + active customer, so we skip
 * silently when the user is not (yet) logged in.
 */

type IngestEventType = "PAGE_VIEW" | "LOGIN" | "BUTTON_CLICK";

type EventPayload = Record<string, unknown>;

const send = (eventType: IngestEventType, payload: EventPayload = {}) => {
   if (typeof window === "undefined") return;
   if (isBrowserMockRuntimeEnabled()) return;

   const customerNumber = authStore.currentCompany;
   const token = authStore.getSessionToken();
   if (!authStore.isLoggedIn() || !customerNumber || !token) {
      return;
   }

   try {
      const url = resolvePlatformRequestUrl(platformRoutes.customerEventIngest, { customerNumber });
      const body = {
         eventType,
         clientTimestamp: new Date().toISOString(),
         payload: { ...payload, pageLocation: window.location.href }
      };
      void apiClient(url, token)
         .options({ keepalive: true })
         .json(body)
         .post()
         .res()
         .catch(() => {});
   } catch (error) {
      logger.warn("tracking", "Best-effort event send threw (ignored)", { eventType, error });
   }
};

export const sendPageViewEvent = () => {
   send("PAGE_VIEW");
};

export const sendLoginEvent = () => {
   send("LOGIN", { method: uiStore.loginMode });
};

export const sendButtonClickEvent = (buttonText?: string) => {
   if (!buttonText) return;
   send("BUTTON_CLICK", { buttonText });
};
